import { createRequestActionCreators, RequestAction, createPayloadAction } from 'helpers/actionsHelpers'
import {
  CommentUid,
  PostUid,
  Post,
  FeedFilterQuery,
  Comment,
  ComposeId,
  ComposeType,
  SelectableFeedFilter,
  WithPostUid,
  SuggestUser,
  FeedUserUid,
  Mention,
  ComposeCommand,
  ComposeInputBehavior,
  SuggestTag,
  ComposeOutput,
} from 'interfaces/feed'
import { PayloadAction } from 'interfaces/action'
import { ImageId, MediaLink } from 'interfaces/media'
import { UserImageId, UserImage } from 'interfaces/userImage'

// Compose - MOUNT / UNMOUNT
// -----------------------------------------------------------------------------
type ComposeExistsPayload =
  & WithComposeId
export const COMPOSE_EXISTS = 'COMPOSE_EXISTS' as const
export const composeExistsActionCreator =
  (payload: ComposeExistsPayload): PayloadAction<typeof COMPOSE_EXISTS, ComposeExistsPayload> =>
    createPayloadAction(COMPOSE_EXISTS, payload)
type ComposeExists = ReturnType<typeof composeExistsActionCreator>

type ComposeMountPayload =
  & WithComposeId
  & {
    type: ComposeType
    postUid?: PostUid
    isOpportunityReview?: boolean

    placeholder?: string
    focusOnMount?: boolean
  }
export const COMPOSE_MOUNT = 'COMPOSE_MOUNT' as const
export const composeMountActionCreator =
  (payload: ComposeMountPayload): PayloadAction<typeof COMPOSE_MOUNT, ComposeMountPayload> =>
    createPayloadAction(COMPOSE_MOUNT, payload)
type ComposeMount = ReturnType<typeof composeMountActionCreator>

export const COMPOSE_UNMOUNT = 'COMPOSE_UNMOUNT' as const
export const composeUnmountActionCreator =
  (payload: WithComposeId): PayloadAction<typeof COMPOSE_UNMOUNT, WithComposeId> =>
    createPayloadAction(COMPOSE_UNMOUNT, payload)
type ComposeUnmount = ReturnType<typeof composeUnmountActionCreator>


type ComposeMountWithPostPayload =
  & WithComposeId
  & WithPostUid
  & {
    placeholder?: string
    focusOnMount?: boolean
  }
type ComposeMountWithPostResponse = {
  post: Post
}
export const COMPOSE_MOUNT_WITH_POST = 'COMPOSE_MOUNT_WITH_POST' as const
type ComposeMountWithPost = RequestAction<typeof COMPOSE_MOUNT_WITH_POST, ComposeMountWithPostPayload, ComposeMountWithPostResponse>
export const composeMountWithPostActionCreator = createRequestActionCreators<typeof COMPOSE_MOUNT_WITH_POST, ComposeMountWithPostPayload, ComposeMountWithPostResponse>(COMPOSE_MOUNT_WITH_POST)


type ComposeMountWithCommentPayload =
  & WithComposeId
  & WithPostUid
  & {
    commentUid: CommentUid
    isOpportunityReview?: boolean

    placeholder?: string
    focusOnMount?: boolean
  }
type ComposeMountWithCommentResponse = {
  comment: Comment
}
export const COMPOSE_MOUNT_WITH_COMMENT = 'COMPOSE_MOUNT_WITH_COMMENT' as const
type ComposeMountWithComment = RequestAction<typeof COMPOSE_MOUNT_WITH_COMMENT, ComposeMountWithCommentPayload, ComposeMountWithCommentResponse>
export const composeMountWithCommentActionCreator = createRequestActionCreators<typeof COMPOSE_MOUNT_WITH_COMMENT, ComposeMountWithCommentPayload, ComposeMountWithCommentResponse>(COMPOSE_MOUNT_WITH_COMMENT)

type ComposeMountWithReplyRequest =
  & WithComposeId
  & WithPostUid
  & {
    replyCommentUid: CommentUid
    parentCommentUid: CommentUid

    placeholder?: string
    focusOnMount?: boolean
  }

type ComposeMountWithReplyResponse = {
  text: string
  mention?: Mention
}
export const COMPOSE_MOUNT_WITH_REPLY = 'COMPOSE_MOUNT_WITH_REPLY' as const
type ComposeMountWithReply = RequestAction<typeof COMPOSE_MOUNT_WITH_REPLY, ComposeMountWithReplyRequest, ComposeMountWithReplyResponse>
export const composeMountWithReplyActionCreator = createRequestActionCreators<typeof COMPOSE_MOUNT_WITH_REPLY, ComposeMountWithReplyRequest, ComposeMountWithReplyResponse>(COMPOSE_MOUNT_WITH_REPLY)


// Compose - LIFECYCLE
// -----------------------------------------------------------------------------

type FeedComposeImageRemovePayload =
  & WithComposeId
  & {
    id: ImageId
  }
export const FEED_COMPOSE_IMAGE_REMOVE = 'FEED_COMPOSE_IMAGE_REMOVE' as const
type FeedComposeImageRemove = RequestAction<typeof FEED_COMPOSE_IMAGE_REMOVE, FeedComposeImageRemovePayload, {}>
export const feedComposeImageRemoveActionCreator = createRequestActionCreators<typeof FEED_COMPOSE_IMAGE_REMOVE, FeedComposeImageRemovePayload, {}>(FEED_COMPOSE_IMAGE_REMOVE)

export const FEED_COMPOSE_CLEAN_UP = 'FEED_COMPOSE_CLEAN_UP' as const
type FeedComposeCleanUp = RequestAction<typeof FEED_COMPOSE_CLEAN_UP, WithComposeId, {}>
export const feedComposeCleanUpActionCreator = createRequestActionCreators<typeof FEED_COMPOSE_CLEAN_UP, WithComposeId, {}>(FEED_COMPOSE_CLEAN_UP)

export const COMPOSE_FOCUS = 'COMPOSE_FOCUS' as const
export const composeFocusActionCreator =
  (payload: WithComposeId): PayloadAction<typeof COMPOSE_FOCUS, WithComposeId> =>
    createPayloadAction(COMPOSE_FOCUS, payload)
type ComposeFocus = ReturnType<typeof composeFocusActionCreator>

export const COMPOSE_BLUR = 'COMPOSE_BLUR' as const
export const composeBlurActionCreator =
  (payload: WithComposeId): PayloadAction<typeof COMPOSE_BLUR, WithComposeId> =>
    createPayloadAction(COMPOSE_BLUR, payload)
type ComposeBlur = ReturnType<typeof composeBlurActionCreator>

// Compose - ACTIONS
// -----------------------------------------------------------------------------
type WithComposeId = {
  composeId: ComposeId
}

type ComposeValidateRequest =
  & WithComposeId
type ComposeValidateResponse = {}
export const COMPOSE_VALIDATE = 'COMPOSE_VALIDATE' as const
export type ComposeValidate = RequestAction<typeof COMPOSE_VALIDATE, ComposeValidateRequest, ComposeValidateResponse>
export const composeValidateActionCreator = createRequestActionCreators<
  typeof COMPOSE_VALIDATE, ComposeValidateRequest, ComposeValidateResponse
>(COMPOSE_VALIDATE)


export type ComposeSubmitRequest =
  & WithComposeId
  & {
    postOutput: ComposeOutput
  }
type ComposeSubmitResponse =
  & {
    isEdit?: boolean
    postUid?: PostUid
    commentUid?: CommentUid
    parentCommentUid?: CommentUid
    isOpportunityReview?: boolean
  }
  & (
    | {
      type: 'post'
      post: Post
      comments?: Comment[]
      images: UserImage[]
      feedFilter?: FeedFilterQuery
    }
    | {
      type: 'comment'
      comment: Comment
      images: UserImage[]
    }
  )
export const COMPOSE_SUBMIT = 'COMPOSE_SUBMIT' as const
export type ComposeSubmit = RequestAction<typeof COMPOSE_SUBMIT, ComposeSubmitRequest, ComposeSubmitResponse>
export const composeSubmitActionCreator = createRequestActionCreators<
  typeof COMPOSE_SUBMIT, ComposeSubmitRequest, ComposeSubmitResponse
>(COMPOSE_SUBMIT)

export type FeedComposeTypeTextPayload = {
  text: string
} & WithComposeId
export const FEED_COMPOSE_TYPE_TEXT = 'FEED_COMPOSE_TYPE_TEXT' as const
export const feedComposeTypeTextActionCreator =
  (payload: FeedComposeTypeTextPayload): PayloadAction<typeof FEED_COMPOSE_TYPE_TEXT, FeedComposeTypeTextPayload> =>
    createPayloadAction(FEED_COMPOSE_TYPE_TEXT, payload)
type FeedComposeTypeText = ReturnType<typeof feedComposeTypeTextActionCreator>

type FeedComposeMentionPayload =
  & WithComposeId
  & {
    mention: string
    uid: FeedUserUid
  }
export const FEED_COMPOSE_MENTION = 'FEED_COMPOSE_MENTION' as const
export const feedComposeMentionActionCreator =
  (payload: FeedComposeMentionPayload): PayloadAction<typeof FEED_COMPOSE_MENTION, FeedComposeMentionPayload> =>
    createPayloadAction(FEED_COMPOSE_MENTION, payload)
type FeedComposeMention = ReturnType<typeof feedComposeMentionActionCreator>

type FeedComposeSelectedFilterPayload = {
  filter: SelectableFeedFilter
} & WithComposeId
export const FEED_COMPOSE_SELECT_FILTER = 'FEED_COMPOSE_SELECT_FILTER' as const
type FeedComposeSelectFilter = PayloadAction<typeof FEED_COMPOSE_SELECT_FILTER, FeedComposeSelectedFilterPayload>
export const feedComposeSelectFilterActionCreator = (payload: FeedComposeSelectedFilterPayload): FeedComposeSelectFilter =>
  createPayloadAction(FEED_COMPOSE_SELECT_FILTER, payload)


type ComposeExecCommandPayload =
  & WithComposeId
  & {
    command: ComposeCommand
  }
export const COMPOSE_EXEC_COMMAND = 'COMPOSE_EXEC_COMMAND' as const
export const composeExecCommandActionCreator =
  (payload: ComposeExecCommandPayload): PayloadAction<typeof COMPOSE_EXEC_COMMAND, ComposeExecCommandPayload> =>
    createPayloadAction(COMPOSE_EXEC_COMMAND, payload)
type ComposeExecCommand = ReturnType<typeof composeExecCommandActionCreator>

type ComposeSetInputBehaviorPayload =
  & WithComposeId
  & {
    type: ComposeInputBehavior
  }
export const COMPOSE_SET_INPUT_BEHAVIOR = 'COMPOSE_SET_INPUT_BEHAVIOR' as const
export const composeSetInputBehaviorActionCreator =
  (payload: ComposeSetInputBehaviorPayload): PayloadAction<typeof COMPOSE_SET_INPUT_BEHAVIOR, ComposeSetInputBehaviorPayload> =>
    createPayloadAction(COMPOSE_SET_INPUT_BEHAVIOR, payload)
type ComposeSetInputBehavior = ReturnType<typeof composeSetInputBehaviorActionCreator>

// Compose - MEDIA
// -----------------------------------------------------------------------------
type FeedComposeFetchLinkActionPayload = {
  url: string
  composeId: ComposeId
}
export const FEED_COMPOSE_FETCH_LINK = 'FEED_COMPOSE_FETCH_LINK' as const
type FeedComposeFetchLink = RequestAction<typeof FEED_COMPOSE_FETCH_LINK, FeedComposeFetchLinkActionPayload, MediaLink>
export const feedComposeFetchLinkActionCreator =
  createRequestActionCreators<typeof FEED_COMPOSE_FETCH_LINK, FeedComposeFetchLinkActionPayload, MediaLink>(FEED_COMPOSE_FETCH_LINK)

export const FEED_COMPOSE_FETCH_LINK_REMOVE = 'FEED_COMPOSE_FETCH_LINK_REMOVE' as const
export const feedComposeFetchLinkRemoveActionCreator =
  (payload: WithComposeId): PayloadAction<typeof FEED_COMPOSE_FETCH_LINK_REMOVE, WithComposeId> =>
    createPayloadAction(FEED_COMPOSE_FETCH_LINK_REMOVE, payload)
type FeedComposeFetchLinkRemove = ReturnType<typeof feedComposeFetchLinkRemoveActionCreator>

type FeedComposeAddImageAttemptRequest =
  & WithComposeId
  & {
    imagesIds?: UserImageId[]
    files?: File[]
  }
type FeedComposeAddImageAttemptResponse = {
  imageId: UserImageId
}
export const FEED_COMPOSE_ADD_IMAGE_ATTEMPT = 'FEED_COMPOSE_ADD_IMAGE_ATTEMPT' as const
export type FeedComposeAddImageAttempt = RequestAction<typeof FEED_COMPOSE_ADD_IMAGE_ATTEMPT, FeedComposeAddImageAttemptRequest, FeedComposeAddImageAttemptResponse>
export const feedComposeAddImageAttemptActionCreator =
  createRequestActionCreators<typeof FEED_COMPOSE_ADD_IMAGE_ATTEMPT, FeedComposeAddImageAttemptRequest, FeedComposeAddImageAttemptResponse>(FEED_COMPOSE_ADD_IMAGE_ATTEMPT)

export type SuggestUsersRequest = {
  search: string
  postUid?: PostUid
}
export type SuggestUsersApiPayload = {
  search: string
  post_uid?: PostUid
}
type SuggestUsersResponse = SuggestUser[]
export const SUGGEST_USERS = 'SUGGEST_USERS' as const
export type SuggestUsers = RequestAction<typeof SUGGEST_USERS, SuggestUsersRequest, SuggestUsersResponse>
export const suggestUsersActionCreator =
  createRequestActionCreators<typeof SUGGEST_USERS, SuggestUsersRequest, SuggestUsersResponse>(SUGGEST_USERS)

export type SuggestTagsRequest = {
  search: string
}
type SuggestTagsResponse = SuggestTag[]
export const SUGGEST_TAGS = 'SUGGEST_TAGS' as const
export type SuggestTags = RequestAction<typeof SUGGEST_TAGS, SuggestTagsRequest, SuggestTagsResponse>
export const suggestTagsActionCreator =
  createRequestActionCreators<typeof SUGGEST_TAGS, SuggestTagsRequest, SuggestTagsResponse>(SUGGEST_TAGS)

type FeedComposeAction =
  | ComposeExists
  | ComposeMount
  | ComposeMountWithPost
  | ComposeMountWithComment
  | ComposeMountWithReply
  | ComposeUnmount

  | ComposeExecCommand
  | ComposeSetInputBehavior

  | ComposeValidate
  | ComposeSubmit

  | FeedComposeImageRemove
  | FeedComposeTypeText
  | FeedComposeCleanUp
  | FeedComposeFetchLink
  | FeedComposeFetchLinkRemove
  | ComposeFocus
  | ComposeBlur
  | FeedComposeSelectFilter
  | FeedComposeAddImageAttempt
  | SuggestUsers
  | SuggestTags
  | FeedComposeMention

export default FeedComposeAction
